/**
 * Frontend Application Performance Monitoring (APM)
 *
 * Instruments React components and browser APIs for:
 * - Component render timing
 * - API request performance
 * - Web Vitals (LCP, FID, CLS, TTFB, FCP)
 * - Error tracking
 * - User interaction timing
 */

// Performance metric types
interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  timestamp: number;
  labels?: Record<string, string>;
}

interface WebVitals {
  lcp?: number;  // Largest Contentful Paint
  fid?: number;  // First Input Delay
  cls?: number;  // Cumulative Layout Shift
  ttfb?: number; // Time to First Byte
  fcp?: number;  // First Contentful Paint
  inp?: number;  // Interaction to Next Paint
}

interface ComponentTiming {
  componentName: string;
  renderCount: number;
  totalRenderTime: number;
  lastRenderTime: number;
  avgRenderTime: number;
  maxRenderTime: number;
}

interface ApiTiming {
  endpoint: string;
  method: string;
  count: number;
  totalDuration: number;
  avgDuration: number;
  maxDuration: number;
  errorCount: number;
  lastCall: number;
}

// APM State
class FrontendAPM {
  private metrics: PerformanceMetric[] = [];
  private maxMetrics = 1000;
  private webVitals: WebVitals = {};
  private componentTimings: Map<string, ComponentTiming> = new Map();
  private apiTimings: Map<string, ApiTiming> = new Map();
  private errors: Array<{ message: string; stack?: string; timestamp: number; traceId?: string }> = [];
  private maxErrors = 100;
  private sessionId: string;
  private traceId?: string;
  private enabled: boolean = true;

  constructor() {
    this.sessionId = this.generateId();
    this.initWebVitals();
    this.initErrorTracking();
    this.initNavigationTracking();
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  // Web Vitals initialization using Performance Observer
  private initWebVitals(): void {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      return;
    }

    // Largest Contentful Paint (LCP)
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        if (lastEntry) {
          this.webVitals.lcp = lastEntry.startTime;
          this.recordMetric('web_vitals_lcp', lastEntry.startTime, 'ms');
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      // LCP not supported
    }

    // First Input Delay (FID)
    try {
      const fidObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const firstEntry = entries[0] as any;
        if (firstEntry) {
          this.webVitals.fid = firstEntry.processingStart - firstEntry.startTime;
          this.recordMetric('web_vitals_fid', this.webVitals.fid, 'ms');
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      // FID not supported
    }

    // Cumulative Layout Shift (CLS)
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries() as any[]) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
        this.webVitals.cls = clsValue;
        this.recordMetric('web_vitals_cls', clsValue, 'score');
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      // CLS not supported
    }

    // First Contentful Paint (FCP) and TTFB from navigation timing
    if ('performance' in window && performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navEntries.length > 0) {
        const navEntry = navEntries[0];
        this.webVitals.ttfb = navEntry.responseStart - navEntry.requestStart;
        this.recordMetric('web_vitals_ttfb', this.webVitals.ttfb, 'ms');
      }

      // FCP from paint entries
      try {
        const paintObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              this.webVitals.fcp = entry.startTime;
              this.recordMetric('web_vitals_fcp', entry.startTime, 'ms');
            }
          }
        });
        paintObserver.observe({ type: 'paint', buffered: true });
      } catch (e) {
        // Paint timing not supported
      }
    }
  }

  // Error tracking
  private initErrorTracking(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', (event) => {
      this.recordError(event.message, event.error?.stack);
    });

    window.addEventListener('unhandledrejection', (event) => {
      const message = event.reason instanceof Error
        ? event.reason.message
        : String(event.reason);
      const stack = event.reason instanceof Error
        ? event.reason.stack
        : undefined;
      this.recordError(`Unhandled Promise Rejection: ${message}`, stack);
    });
  }

  // Navigation timing
  private initNavigationTracking(): void {
    if (typeof window === 'undefined') return;

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Send metrics before page is hidden
        this.flushMetrics();
      }
    });

    // Track route changes for SPA
    const originalPushState = history.pushState;
    history.pushState = (...args) => {
      const result = originalPushState.apply(history, args);
      this.recordNavigation(window.location.pathname);
      return result;
    };

    window.addEventListener('popstate', () => {
      this.recordNavigation(window.location.pathname);
    });
  }

  // Record a generic metric
  recordMetric(name: string, value: number, unit: string, labels?: Record<string, string>): void {
    if (!this.enabled) return;

    this.metrics.push({
      name,
      value,
      unit,
      timestamp: Date.now(),
      labels,
    });

    // Trim if too many metrics
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }
  }

  // Record an error
  recordError(message: string, stack?: string): void {
    if (!this.enabled) return;

    this.errors.push({
      message,
      stack,
      timestamp: Date.now(),
      traceId: this.traceId,
    });

    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    console.error('[APM Error]', message);
  }

  // Record navigation
  recordNavigation(path: string): void {
    this.recordMetric('navigation', performance.now(), 'ms', { path });
  }

  // Component render timing
  recordComponentRender(componentName: string, renderTime: number): void {
    if (!this.enabled) return;

    const timing = this.componentTimings.get(componentName) || {
      componentName,
      renderCount: 0,
      totalRenderTime: 0,
      lastRenderTime: 0,
      avgRenderTime: 0,
      maxRenderTime: 0,
    };

    timing.renderCount++;
    timing.totalRenderTime += renderTime;
    timing.lastRenderTime = renderTime;
    timing.avgRenderTime = timing.totalRenderTime / timing.renderCount;
    timing.maxRenderTime = Math.max(timing.maxRenderTime, renderTime);

    this.componentTimings.set(componentName, timing);

    // Record as metric if slow (> 16ms = 60fps threshold)
    if (renderTime > 16) {
      this.recordMetric('slow_component_render', renderTime, 'ms', { component: componentName });
    }
  }

  // API call timing
  recordApiCall(
    endpoint: string,
    method: string,
    duration: number,
    success: boolean,
    statusCode?: number
  ): void {
    if (!this.enabled) return;

    const key = `${method} ${endpoint}`;
    const timing = this.apiTimings.get(key) || {
      endpoint,
      method,
      count: 0,
      totalDuration: 0,
      avgDuration: 0,
      maxDuration: 0,
      errorCount: 0,
      lastCall: 0,
    };

    timing.count++;
    timing.totalDuration += duration;
    timing.avgDuration = timing.totalDuration / timing.count;
    timing.maxDuration = Math.max(timing.maxDuration, duration);
    timing.lastCall = Date.now();
    if (!success) timing.errorCount++;

    this.apiTimings.set(key, timing);

    this.recordMetric('api_call_duration', duration, 'ms', {
      endpoint: this.normalizeEndpoint(endpoint),
      method,
      status: String(statusCode || (success ? 200 : 500)),
    });
  }

  // Normalize API endpoint for metrics (remove IDs)
  private normalizeEndpoint(endpoint: string): string {
    return endpoint
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
      .replace(/\/\d+(?=\/|$)/g, '/:id')
      .replace(/cs_[a-zA-Z0-9]+/g, ':sessionId')
      .replace(/[a-f0-9]{64}/gi, ':token');
  }

  // Set trace ID for correlation with backend
  setTraceId(traceId: string): void {
    this.traceId = traceId;
  }

  // Get current trace ID
  getTraceId(): string | undefined {
    return this.traceId;
  }

  // Get session ID
  getSessionId(): string {
    return this.sessionId;
  }

  // Get all collected data
  getData(): {
    sessionId: string;
    webVitals: WebVitals;
    componentTimings: ComponentTiming[];
    apiTimings: ApiTiming[];
    recentMetrics: PerformanceMetric[];
    errors: Array<{ message: string; stack?: string; timestamp: number; traceId?: string }>;
  } {
    return {
      sessionId: this.sessionId,
      webVitals: this.webVitals,
      componentTimings: Array.from(this.componentTimings.values()),
      apiTimings: Array.from(this.apiTimings.values()),
      recentMetrics: this.metrics.slice(-100),
      errors: this.errors.slice(-20),
    };
  }

  // Flush metrics to backend (called on page unload or periodically)
  async flushMetrics(): Promise<void> {
    if (!this.enabled || this.metrics.length === 0) return;

    const data = this.getData();

    try {
      // Use sendBeacon for reliability on page unload
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          '/api/apm/frontend-metrics',
          JSON.stringify(data)
        );
      } else {
        await fetch('/api/apm/frontend-metrics', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
          keepalive: true,
        });
      }
    } catch (e) {
      // Silently fail - don't block user experience
    }
  }

  // Enable/disable APM
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  // Reset all data
  reset(): void {
    this.metrics = [];
    this.componentTimings.clear();
    this.apiTimings.clear();
    this.errors = [];
  }
}

// Singleton instance
export const apm = new FrontendAPM();

// Performance measurement utilities

/**
 * Measure the execution time of an async function
 */
export async function measureAsync<T>(
  name: string,
  fn: () => Promise<T>,
  labels?: Record<string, string>
): Promise<T> {
  const start = performance.now();
  try {
    return await fn();
  } finally {
    const duration = performance.now() - start;
    apm.recordMetric(name, duration, 'ms', labels);
  }
}

/**
 * Measure the execution time of a sync function
 */
export function measure<T>(
  name: string,
  fn: () => T,
  labels?: Record<string, string>
): T {
  const start = performance.now();
  try {
    return fn();
  } finally {
    const duration = performance.now() - start;
    apm.recordMetric(name, duration, 'ms', labels);
  }
}

/**
 * Create a performance mark
 */
export function markStart(name: string): void {
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(`${name}-start`);
  }
}

/**
 * End a performance mark and record the duration
 */
export function markEnd(name: string, labels?: Record<string, string>): void {
  if (typeof performance !== 'undefined' && performance.mark && performance.measure) {
    performance.mark(`${name}-end`);
    try {
      const measure = performance.measure(name, `${name}-start`, `${name}-end`);
      apm.recordMetric(name, measure.duration, 'ms', labels);
    } catch (e) {
      // Mark might not exist
    }
  }
}

/**
 * Instrumented fetch wrapper
 */
export async function instrumentedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  const method = init?.method || 'GET';
  const startTime = performance.now();

  try {
    const response = await fetch(input, init);
    const duration = performance.now() - startTime;

    // Extract trace ID from response headers
    const traceId = response.headers.get('X-Trace-Id');
    if (traceId) {
      apm.setTraceId(traceId);
    }

    apm.recordApiCall(url, method, duration, response.ok, response.status);

    return response;
  } catch (error) {
    const duration = performance.now() - startTime;
    apm.recordApiCall(url, method, duration, false);
    throw error;
  }
}

// Export types
export type { PerformanceMetric, WebVitals, ComponentTiming, ApiTiming };
